import { SDKStateData, StateChangeListener } from './types';

export class SDKState {
  private readonly state: SDKStateData = {
    muted: false,
    paused: false,
    adFree: false,
    locale: 'en-US',
  };
  private listeners: Map<keyof SDKStateData, Set<StateChangeListener<keyof SDKStateData>>> = new Map();

  constructor(initialState: Partial<SDKStateData> = {}) {
    this.state = { ...this.state, ...initialState };
  }

  public get<K extends keyof SDKStateData>(key: K): SDKStateData[K] {
    return this.state[key];
  }

  public set<K extends keyof SDKStateData>(key: K, value: SDKStateData[K]): void {
    const oldValue = this.state[key];
    if (oldValue === value) return;

    this.state[key] = value;
    this.notify(key, value, oldValue);
  }

  public getAll(): Readonly<SDKStateData> {
    return { ...this.state };
  }

  public subscribe<K extends keyof SDKStateData>(key: K, listener: StateChangeListener<K>): () => void {
    if (!this.listeners.has(key)) {
      this.listeners.set(key, new Set());
    }

    this.listeners.get(key)!.add(listener as StateChangeListener<keyof SDKStateData>);

    return () => {
      const keyListeners = this.listeners.get(key);
      if (keyListeners) {
        keyListeners.delete(listener as StateChangeListener<keyof SDKStateData>);
      }
    };
  }

  private notify<K extends keyof SDKStateData>(key: K, value: SDKStateData[K], oldValue: SDKStateData[K]): void {
    this.listeners.get(key)?.forEach((listener) => listener(value, oldValue));
  }

  public clear(): void {
    this.listeners.clear();
  }
}
